import React, { useEffect, useMemo, useState } from 'react';
import {
    Modal,
    Table,
    Input,
    InputNumber,
    Switch,
    Tag,
    Space,
    Button,
    Tooltip,
    Empty,
    message
} from 'antd';
import type { TableColumnsType } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useLazyQuery, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
    LIST_COMMON_CONFIGS_BY_CONFIG_KEY,
    ListCommonConfigsByConfigKeyResponse,
    ListCommonConfigsByConfigKeyVariables,
    UPDATE_COMMON_CONFIG,
    UpdateCommonConfigResponse,
    MATCH_COMMON_CONFIGS,
    MatchCommonConfigsResponse,
    MatchCommonConfigsVariables,
    FIND_COMMON_CONFIG_DUPLICATES,
    FindCommonConfigDuplicatesResponse,
    FindCommonConfigDuplicatesVariables,
    CommonConfigType
} from '../graphql/commonConfigQueries';

interface HeadEntry {
    key: string;
    value: string;
}

interface ConfigDebugModalProps {
    visible: boolean;
    configKey: string;
    onClose: () => void;
    onConfigChanged?: () => void;
}

/**
 * sortOrder 单元格：保持自己的本地数值状态，仅在 blur 且值变更时回调提交。
 * 与父组件 refetch 后的最新值同步通过 [rowVersion] / [initialValue] 触发 useEffect 完成。
 */
const SortOrderCell: React.FC<{
    initialValue: number;
    rowVersion: number;
    disabled?: boolean;
    onCommit: (next: number) => void;
}> = ({ initialValue, rowVersion, disabled, onCommit }) => {
    const [val, setVal] = useState<number | null>(initialValue);

    useEffect(() => {
        setVal(initialValue);
    }, [initialValue, rowVersion]);

    return (
        <InputNumber
            size="small"
            style={{ width: 90 }}
            value={val}
            onChange={(v) => setVal(typeof v === 'number' ? v : null)}
            onBlur={() => {
                if (val === null || val === undefined) {
                    setVal(initialValue);
                    return;
                }
                if (val !== initialValue) {
                    onCommit(val);
                }
            }}
            disabled={disabled}
        />
    );
};

const ConfigDebugModal: React.FC<ConfigDebugModalProps> = ({
    visible,
    configKey,
    onClose,
    onConfigChanged
}) => {
    const { t } = useTranslation();

    const [headEntries, setHeadEntries] = useState<HeadEntry[]>([{ key: '', value: '' }]);
    const [debugResult, setDebugResult] = useState<MatchCommonConfigsResponse | null>(null);
    const [refreshVersion, setRefreshVersion] = useState(0);

    const { data, loading, refetch } = useQuery<
        ListCommonConfigsByConfigKeyResponse,
        ListCommonConfigsByConfigKeyVariables
    >(LIST_COMMON_CONFIGS_BY_CONFIG_KEY, {
        variables: { configKey },
        skip: !visible || !configKey,
        fetchPolicy: 'network-only'
    });

    useEffect(() => {
        if (visible) {
            setHeadEntries([{ key: '', value: '' }]);
            setDebugResult(null);
        }
    }, [visible, configKey]);

    const [updateConfig] = useMutation<UpdateCommonConfigResponse>(UPDATE_COMMON_CONFIG);
    const apollo = useApolloClient();

    /**
     * 同 (configKey, sortOrder) 下「打开」状态最多一条；若目标行将处于 active，
     * 在调 update 前先查 active 冲突。返回冲突行（不为空时调用方应阻塞并提示）。
     */
    const checkActiveConflicts = async (
        targetSortOrder: number,
        excludeId: string
    ): Promise<CommonConfigType[]> => {
        const resp = await apollo.query<
            FindCommonConfigDuplicatesResponse,
            FindCommonConfigDuplicatesVariables
        >({
            query: FIND_COMMON_CONFIG_DUPLICATES,
            variables: {
                configKey,
                sortOrder: targetSortOrder,
                excludeId
            },
            fetchPolicy: 'network-only'
        });
        return (resp.data?.findCommonConfigDuplicates ?? []).filter(d => d.isActive);
    };

    const showActiveConflictModal = (sortOrder: number, conflicts: CommonConfigType[]) => {
        Modal.warning({
            title: t('commonConfig.confirm.activeConflict.title'),
            width: 640,
            content: (
                <div>
                    <div style={{ marginBottom: 8 }}>
                        {t('commonConfig.confirm.activeConflict.content', {
                            configKey,
                            sortOrder
                        })}
                    </div>
                    <div
                        style={{
                            maxHeight: 240,
                            overflow: 'auto',
                            border: '1px solid #f0f0f0',
                            borderRadius: 4
                        }}
                    >
                        {conflicts.map(d => (
                            <div
                                key={d.id}
                                style={{
                                    padding: 8,
                                    borderBottom: '1px solid #f5f5f5',
                                    fontSize: 12
                                }}
                            >
                                <div><strong>ID:</strong> {d.id}</div>
                                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                    <strong>{t('commonConfig.table.columns.matchRule')}:</strong>{' '}
                                    {d.matchRule || '-'}
                                </div>
                                <div>
                                    <strong>{t('commonConfig.table.columns.configValue')}:</strong>{' '}
                                    {d.configValue || '-'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ),
            okText: t('commonConfig.confirm.okText'),
            okButtonProps: {
                style: { backgroundColor: '#722ed1', borderColor: '#722ed1' }
            }
        });
    };

    /**
     * inline 更新：在调 mutation 前做 active 冲突预检（仅当本次目标 isActive 为 true）。
     * 不预检直接走 mutation 会触发后端 IllegalStateException → GraphQL Internal Error，对用户不友好。
     */
    const handleInlineUpdate = async (
        row: CommonConfigType,
        input: { sortOrder?: number; isActive?: boolean }
    ) => {
        const targetIsActive = input.isActive ?? row.isActive;
        const targetSortOrder = input.sortOrder ?? row.sortOrder;
        if (targetIsActive) {
            try {
                const conflicts = await checkActiveConflicts(targetSortOrder, row.id);
                if (conflicts.length > 0) {
                    showActiveConflictModal(targetSortOrder, conflicts);
                    await refetch();
                    setRefreshVersion(v => v + 1);
                    return;
                }
            } catch (e: any) {
                message.error(
                    t('commonConfig.debugConfig.messages.updateFailed', {
                        error: e?.message ?? ''
                    })
                );
                await refetch();
                setRefreshVersion(v => v + 1);
                return;
            }
        }
        try {
            await updateConfig({ variables: { id: row.id, input } });
            message.success(t('commonConfig.debugConfig.messages.updateSuccess'));
            await refetch();
            setRefreshVersion(v => v + 1);
            onConfigChanged?.();
        } catch (e: any) {
            message.error(
                t('commonConfig.debugConfig.messages.updateFailed', {
                    error: e?.message ?? ''
                })
            );
            await refetch();
            setRefreshVersion(v => v + 1);
        }
    };

    // 排序：active 先于 inactive；组内按 sortOrder ASC, id ASC
    const rows = useMemo<CommonConfigType[]>(() => {
        const list = data?.listCommonConfigsByConfigKey ?? [];
        return [...list].sort((a, b) => {
            if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
            if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
            return Number(a.id) - Number(b.id);
        });
    }, [data]);

    const detectedVars = useMemo<string[]>(() => {
        const merged = rows.map((r) => r.matchRule || '').join('\n');
        const matches = merged.matchAll(/#([A-Za-z_$][\w$]*)/g);
        const seen = new Set<string>();
        const list: string[] = [];
        for (const m of matches) {
            const name = m[1];
            if (!seen.has(name)) {
                seen.add(name);
                list.push(name);
            }
        }
        return list;
    }, [rows]);

    const addedKeys = useMemo<Set<string>>(
        () => new Set(headEntries.map((e) => e.key.trim()).filter((k) => k.length > 0)),
        [headEntries]
    );

    const handleAddEntry = () => {
        setHeadEntries([...headEntries, { key: '', value: '' }]);
    };

    const handleRemoveEntry = (index: number) => {
        const next = headEntries.filter((_, i) => i !== index);
        setHeadEntries(next.length === 0 ? [{ key: '', value: '' }] : next);
    };

    const handleEntryChange = (index: number, field: 'key' | 'value', val: string) => {
        const next = [...headEntries];
        next[index] = { ...next[index], [field]: val };
        setHeadEntries(next);
    };

    const handleVarChipClick = (name: string) => {
        if (addedKeys.has(name)) return;
        const next = [...headEntries];
        const lastIdx = next.length - 1;
        if (lastIdx >= 0 && next[lastIdx].key.trim().length === 0) {
            next[lastIdx] = { ...next[lastIdx], key: name };
        } else {
            next.push({ key: name, value: '' });
        }
        setHeadEntries(next);
    };

    const [runMatch, { loading: matchLoading }] = useLazyQuery<
        MatchCommonConfigsResponse,
        MatchCommonConfigsVariables
    >(MATCH_COMMON_CONFIGS, {
        fetchPolicy: 'network-only',
        onCompleted: (resp) => setDebugResult(resp),
        onError: (err) => {
            setDebugResult(null);
            message.error(
                t('commonConfig.debugConfig.messages.runFailed', { error: err.message })
            );
        }
    });

    const handleRun = () => {
        const head = headEntries
            .filter((e) => e.key.trim().length > 0)
            .map((e) => ({ key: e.key.trim(), value: e.value }));
        runMatch({ variables: { configKeys: [configKey], head } });
    };

    const columns: TableColumnsType<CommonConfigType> = [
        {
            title: t('commonConfig.debugConfig.columns.id'),
            dataIndex: 'id',
            key: 'id',
            width: 80
        },
        {
            title: t('commonConfig.debugConfig.columns.sortOrder'),
            key: 'sortOrder',
            width: 110,
            render: (_, r) => (
                <SortOrderCell
                    initialValue={r.sortOrder}
                    rowVersion={refreshVersion}
                    onCommit={(next) => handleInlineUpdate(r, { sortOrder: next })}
                />
            )
        },
        {
            title: t('commonConfig.debugConfig.columns.matchRule'),
            dataIndex: 'matchRule',
            key: 'matchRule',
            render: (text: string) => {
                if (!text) return <span className="text-gray-400">-</span>;
                return (
                    <Tooltip
                        title={<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{text}</div>}
                        placement="topLeft"
                        overlayStyle={{ maxWidth: 480 }}
                    >
                        <div
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 260,
                                cursor: 'help'
                            }}
                        >
                            {text}
                        </div>
                    </Tooltip>
                );
            }
        },
        {
            title: t('commonConfig.debugConfig.columns.isActive'),
            key: 'isActive',
            width: 80,
            render: (_, r) => (
                <Switch
                    checked={r.isActive}
                    onChange={(v) => handleInlineUpdate(r, { isActive: v })}
                />
            )
        },
        {
            title: t('commonConfig.debugConfig.columns.configValue'),
            dataIndex: 'configValue',
            key: 'configValue',
            render: (text: string) => {
                if (!text) return <span className="text-gray-400">-</span>;
                return (
                    <Tooltip
                        title={<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{text}</div>}
                        placement="topLeft"
                        overlayStyle={{ maxWidth: 480 }}
                    >
                        <div
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 180,
                                cursor: 'help'
                            }}
                        >
                            {text}
                        </div>
                    </Tooltip>
                );
            }
        }
    ];

    return (
        <Modal
            title={t('commonConfig.debugConfig.title', { configKey })}
            open={visible}
            width={960}
            onCancel={onClose}
            maskClosable={false}
            footer={
                <Button onClick={onClose}>{t('commonConfig.debugConfig.closeButton')}</Button>
            }
        >
            <div className="space-y-4">
                <div>
                    <div className="mb-2 font-medium">
                        {t('commonConfig.debugConfig.rules.title')}
                    </div>
                    <Table<CommonConfigType>
                        rowKey="id"
                        size="small"
                        loading={loading}
                        columns={columns}
                        dataSource={rows}
                        pagination={false}
                        scroll={{ x: 'max-content', y: 320 }}
                    />
                </div>

                <div>
                    <div className="mb-2 font-medium">
                        {t('commonConfig.debugConfig.detectedVars.title')}
                    </div>
                    {detectedVars.length === 0 ? (
                        <span className="text-gray-400">
                            {t('commonConfig.debugConfig.detectedVars.empty')}
                        </span>
                    ) : (
                        <Space size={[6, 6]} wrap>
                            {detectedVars.map((name) => {
                                const added = addedKeys.has(name);
                                return (
                                    <Tag
                                        key={name}
                                        color={added ? 'default' : 'blue'}
                                        style={{
                                            cursor: added ? 'not-allowed' : 'pointer',
                                            userSelect: 'none'
                                        }}
                                        onClick={() => handleVarChipClick(name)}
                                        title={
                                            added
                                                ? (t('commonConfig.debugConfig.detectedVars.alreadyAdded') as string)
                                                : undefined
                                        }
                                    >
                                        #{name}
                                    </Tag>
                                );
                            })}
                        </Space>
                    )}
                </div>

                <div>
                    <div className="mb-2 flex items-center justify-between">
                        <div className="font-medium">{t('commonConfig.debugConfig.head.title')}</div>
                        <Button
                            type="link"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={handleAddEntry}
                        >
                            {t('commonConfig.debugConfig.head.addButton')}
                        </Button>
                    </div>
                    <div className="space-y-2">
                        {headEntries.map((entry, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                                <Input
                                    style={{ width: 220 }}
                                    placeholder={
                                        t('commonConfig.debugConfig.head.keyPlaceholder') as string
                                    }
                                    value={entry.key}
                                    onChange={(e) => handleEntryChange(idx, 'key', e.target.value)}
                                />
                                <span
                                    className="select-none text-gray-500"
                                    style={{ width: 12, textAlign: 'center' }}
                                >
                                    =
                                </span>
                                <Input
                                    className="flex-1"
                                    placeholder={
                                        t('commonConfig.debugConfig.head.valuePlaceholder') as string
                                    }
                                    value={entry.value}
                                    onChange={(e) => handleEntryChange(idx, 'value', e.target.value)}
                                />
                                <Button
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleRemoveEntry(idx)}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <Button type="primary" loading={matchLoading} onClick={handleRun}>
                        {t('commonConfig.debugConfig.runButton')}
                    </Button>
                </div>

                <div>
                    <div className="mb-2 font-medium">
                        {t('commonConfig.debugConfig.result.title')}
                    </div>
                    <div
                        className="rounded border border-gray-200 p-3 bg-gray-50"
                        style={{ minHeight: 80 }}
                    >
                        {debugResult ? (
                            <pre
                                style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                    margin: 0,
                                    fontFamily:
                                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                    fontSize: 12
                                }}
                            >
                                {JSON.stringify(debugResult.matchCommonConfigs, null, 2)}
                            </pre>
                        ) : (
                            <Empty
                                description={t('commonConfig.debugConfig.result.empty')}
                                imageStyle={{ height: 40 }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ConfigDebugModal;
